import React from 'react';
import {
  PipelineStatusProps,
  PipelineState,
  PIPELINE_STATE_COLORS,
  PIPELINE_STATE_LABELS
} from '../types/airflow';
import { formatDuration } from '../utils/airflowApi';
import { usePipelineStatus } from '../hooks';

const PipelineStatus: React.FC<PipelineStatusProps> = ({
  dagId,
  runId,
  onStatusChange,
  autoRefresh = true,
  refreshInterval = 30000 // 30 seconds
}) => {
  const {
    status,
    isLoading: loading,
    error,
    isPolling,
    refetch,
    dataUpdatedAt
  } = usePipelineStatus({
    dagId,
    runId,
    onStatusChange,
    autoRefresh,
    refreshInterval,
  });

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  const getStateIcon = (state: PipelineState) => {
    switch (state) {
      case PipelineState.QUEUED:
        return '⏳';
      case PipelineState.RUNNING:
        return '🔄';
      case PipelineState.SUCCESS:
        return '✅';
      case PipelineState.FAILED:
        return '❌';
      case PipelineState.SKIPPED:
        return '⏭️';
      case PipelineState.UP_FOR_RETRY:
        return '🔁';
      case PipelineState.UP_FOR_RESCHEDULE:
        return '📅';
      case PipelineState.UPSTREAM_FAILED:
        return '⬆️❌';
      case PipelineState.RESTARTING:
        return '🔄';
      default:
        return '❓';
    }
  };

  const calculateElapsedTime = () => {
    if (!status?.start_date) return null;

    const startTime = new Date(status.start_date);
    const endTime = status.end_date ? new Date(status.end_date) : new Date();
    const elapsedSeconds = (endTime.getTime() - startTime.getTime()) / 1000;

    return formatDuration(elapsedSeconds);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString([], {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="pipeline-status loading">
        <div className="status-header">
          <span className="status-icon">⏳</span>
          <span className="status-text">Loading pipeline status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    const is404 = (error as any)?.response?.status === 404 ||
                  (error as any)?.status === 404 ||
                  error?.message?.includes('404');

    if (is404) {
      return (
        <div className="pipeline-status not-found">
          <div className="status-header">
            <span className="status-icon">📭</span>
            <span className="status-text">No pipeline run found for this date</span>
            <button
              className="retry-btn"
              onClick={() => refetch()}
              title="Refresh"
            >
              🔄
            </button>
          </div>
          <div className="status-info">Try triggering a new pipeline run or select a different date.</div>
        </div>
      );
    }

    return (
      <div className="pipeline-status error">
        <div className="status-header">
          <span className="status-icon">⚠️</span>
          <span className="status-text">Error loading status</span>
          <button
            className="retry-btn"
            onClick={() => refetch()}
            title="Retry"
          >
            🔄
          </button>
        </div>
        <div className="status-error">Unable to fetch pipeline status. Please try again.</div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="pipeline-status not-found">
        <div className="status-header">
          <span className="status-icon">❓</span>
          <span className="status-text">Pipeline status not found</span>
        </div>
      </div>
    );
  }

  const state = status.state as PipelineState;
  const stateColor = PIPELINE_STATE_COLORS[state] || '#6b7280';
  const stateLabel = PIPELINE_STATE_LABELS[state] || status.state;
  const elapsedTime = calculateElapsedTime();

  return (
    <div className="pipeline-status">
      <div className="status-header">
        <span className="status-icon">{getStateIcon(state)}</span>
        <span
          className="status-text"
          style={{ color: stateColor }}
        >
          {stateLabel}
        </span>
        {isPolling && (
          <span className="status-live-indicator" title="Smart polling active - pauses when window inactive">
            🔴
          </span>
        )}
        <button
          className="refresh-btn"
          onClick={() => refetch()}
          title="Refresh status"
        >
          🔄
        </button>
      </div>

      <div className="status-details">
        <div className="status-row">
          <span className="status-label">Run ID:</span>
          <span className="status-value run-id">{runId}</span>
        </div>

        {status.start_date && (
          <div className="status-row">
            <span className="status-label">Started:</span>
            <span className="status-value">{formatTimestamp(status.start_date)}</span>
          </div>
        )}

        {status.end_date && (
          <div className="status-row">
            <span className="status-label">Ended:</span>
            <span className="status-value">{formatTimestamp(status.end_date)}</span>
          </div>
        )}

        {elapsedTime && (
          <div className="status-row">
            <span className="status-label">Duration:</span>
            <span className="status-value">{elapsedTime}</span>
          </div>
        )}

        {lastUpdated && (
          <div className="status-row">
            <span className="status-label">Last updated:</span>
            <span className="status-value last-updated">
              {formatTimestamp(lastUpdated.toISOString())}
            </span>
          </div>
        )}
      </div>

      {[PipelineState.QUEUED, PipelineState.RUNNING].includes(state) && (
        <div className="status-progress">
          <div className="progress-bar">
            <div className="progress-indicator" />
          </div>
          <div className="progress-text">
            {state === PipelineState.QUEUED ? 'Waiting in queue...' : 'Processing...'}
          </div>
        </div>
      )}

      {[PipelineState.FAILED, PipelineState.UPSTREAM_FAILED].includes(state) && (
        <div className="status-error-message">
          ⚠️ Pipeline failed. Please check the logs or contact support.
        </div>
      )}
    </div>
  );
};

interface PipelineStatusBadgeProps {
  state: PipelineState;
  showIcon?: boolean;
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const PipelineStatusBadge: React.FC<PipelineStatusBadgeProps> = ({
  state,
  showIcon = true,
  showLabel = true,
  size = 'medium'
}) => {
  const getStateIcon = (state: PipelineState) => {
    switch (state) {
      case PipelineState.QUEUED:
        return '⏳';
      case PipelineState.RUNNING:
        return '🔄';
      case PipelineState.SUCCESS:
        return '✅';
      case PipelineState.FAILED:
        return '❌';
      case PipelineState.SKIPPED:
        return '⏭️';
      case PipelineState.UP_FOR_RETRY:
        return '🔁';
      case PipelineState.UP_FOR_RESCHEDULE:
        return '📅';
      case PipelineState.UPSTREAM_FAILED:
        return '⬆️❌';
      case PipelineState.RESTARTING:
        return '🔄';
      default:
        return '❓';
    }
  };

  const stateColor = PIPELINE_STATE_COLORS[state] || '#6b7280';
  const stateLabel = PIPELINE_STATE_LABELS[state] || state;

  return (
    <span
      className={`pipeline-status-badge ${size}`}
      style={{
        color: stateColor,
        borderColor: stateColor
      }}
    >
      {showIcon && (
        <span className="badge-icon">{getStateIcon(state)}</span>
      )}
      {showLabel && (
        <span className="badge-label">{stateLabel}</span>
      )}
    </span>
  );
};

export default PipelineStatus;
